/** Web dialog twin of the native Create CV skill-picker bottom sheet. */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { SkillSummary } from "@tabbio/types";
import { WebDialog } from "../../common/WebDialog.web";
import { MaterialSymbol } from "../../icons/MaterialSymbol";
import { Download } from "@/components/icons/HugeIcons";
import { SkillCatalogIcon } from "../../skills/SkillCatalogIcon";
import { TabbioVerifiedBadge } from "../../skills/TabbioVerifiedBadge";
import { formatSkillCount, prettySkillName } from "../../skills/skill-name";
import { useSkillsInfinite } from "../../skills/use-skills-query";
import { cvSkillsForSource, type CreateCvSource } from "./create-cv-machine";
import {
  CV_SKILL_COUNTRY_FILTERS,
  CV_SKILL_LANGUAGE_FILTERS,
  CV_SKILL_LEVEL_FILTERS,
  DEFAULT_CV_SKILL_PICKER_FILTERS,
  activeCvSkillFilterCount,
  filterCvSkillsForPicker,
  localizedCvSkillCopy,
  type CvSkillCountryFilter,
  type CvSkillLanguageFilter,
  type CvSkillPickerFilters,
} from "./cv-skill-picker-filters";
import type { SkillLevel } from "../../skills/skill-taxonomy";

interface Props {
  source: CreateCvSource | null;
  selectedSkillId: string | null;
  onSelect: (skill: SkillSummary) => void;
}

/**
 * The open menu is portalled to `document.body` with fixed positioning: the
 * dialog's content area is an `overflow` scroll container, so an absolutely
 * positioned menu inside it gets clipped whenever the dialog is short (empty
 * results, few rows). Fixed coordinates are measured from the chip and the
 * menu simply closes on scroll/resize instead of tracking the anchor.
 */
type FilterMenuPosition = {
  top: number;
  left?: number;
  right?: number;
  width: number;
  maxHeight: number;
};

function FilterDropdown<V extends string>({
  label,
  options,
  value,
  defaultValue,
  onChange,
  align = "start",
}: {
  label: string;
  options: { value: V; label: string }[];
  value: V;
  defaultValue: V;
  onChange: (value: V) => void;
  align?: "start" | "end";
}) {
  const [position, setPosition] = useState<FilterMenuPosition | null>(null);
  const [hovered, setHovered] = useState<V | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const open = position !== null;
  const current =
    options.find((option) => option.value === value) ?? options[0];
  const active = value !== defaultValue;

  const close = () => {
    setPosition(null);
    setHovered(null);
  };

  const toggle = () => {
    if (open) {
      close();
      return;
    }
    const anchor = rootRef.current?.getBoundingClientRect();
    if (!anchor) return;
    const isRTL =
      rootRef.current != null &&
      getComputedStyle(rootRef.current).direction === "rtl";
    // Anchor the menu to the chip's inline-start or inline-end edge.
    const anchorAtLeftEdge = align === "start" ? !isRTL : isRTL;
    setPosition({
      top: anchor.bottom + 6,
      ...(anchorAtLeftEdge
        ? { left: anchor.left }
        : { right: window.innerWidth - anchor.right }),
      width: Math.max(anchor.width, 190),
      maxHeight: Math.min(280, window.innerHeight - anchor.bottom - 18),
    });
  };

  useEffect(() => {
    if (!open) return;

    const closeOnOutsidePress = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      close();
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    // The fixed-position menu cannot track the chip through a scroll, so any
    // scroll (dialog content included: hence capture) just dismisses it.
    const closeOnScroll = () => close();

    document.addEventListener("mousedown", closeOnOutsidePress);
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", closeOnScroll);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsidePress);
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", closeOnScroll);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !open) return;
        event.stopPropagation();
        close();
      }}
      style={FILTER_DROPDOWN_ROOT}
    >
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${label}: ${current?.label ?? label}`}
        onClick={toggle}
        style={{
          ...FILTER_CHIP,
          borderColor: active ? "var(--text-secondary)" : "var(--input-border)",
          background: active ? "var(--hover)" : "var(--bg)",
        }}
      >
        <span style={FILTER_CHIP_TEXT}>{active ? current?.label : label}</span>
        <MaterialSymbol
          name="expand_more"
          size={17}
          style={{
            color: "var(--text-tertiary)",
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 150ms",
          }}
        />
      </button>

      {position
        ? createPortal(
            <div
              ref={menuRef}
              role="listbox"
              aria-label={label}
              style={{
                ...FILTER_MENU,
                top: position.top,
                left: position.left,
                right: position.right,
                width: position.width,
                maxHeight: position.maxHeight,
              }}
            >
              {options.map((option) => {
                const selected = option.value === value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onMouseEnter={() => setHovered(option.value)}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => {
                      onChange(option.value);
                      close();
                    }}
                    style={{
                      ...FILTER_MENU_OPTION,
                      background:
                        selected || hovered === option.value
                          ? "var(--hover)"
                          : "transparent",
                      fontWeight: selected ? 600 : 400,
                    }}
                  >
                    <span>{option.label}</span>
                    {selected ? (
                      <MaterialSymbol name="check" size={16} />
                    ) : null}
                  </button>
                );
              })}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export function CvSkillPicker({ source, selectedSkillId, onSelect }: Props) {
  const { t, i18n } = useTranslation(["seeker", "common"]);
  const [visible, setVisible] = useState(false);
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<CvSkillPickerFilters>(
    DEFAULT_CV_SKILL_PICKER_FILTERS,
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = activeCvSkillFilterCount(filters);
  const skillsQuery = useSkillsInfinite({ shelf: "explore", type: "cv" });
  const all = useMemo(
    () => skillsQuery.data?.pages.flatMap((page) => page.skills) ?? [],
    [skillsQuery.data],
  );
  const fitting = useMemo(() => cvSkillsForSource(all, source), [all, source]);
  const shown = useMemo(
    () => filterCvSkillsForPicker(all, source, filters, query),
    [all, filters, query, source],
  );
  const selectedSkill = useMemo(
    () => all.find((skill) => skill.skillId === selectedSkillId) ?? null,
    [all, selectedSkillId],
  );
  const selectedLabel = selectedSkill
    ? prettySkillName(localizedCvSkillCopy(selectedSkill, i18n.language).name)
    : selectedSkillId
      ? `/${selectedSkillId}`
      : t("cv.create.tailor.skillPickerOpen");

  const updateFilter = <K extends keyof CvSkillPickerFilters>(
    key: K,
    value: CvSkillPickerFilters[K],
  ) => setFilters((current) => ({ ...current, [key]: value }));

  return (
    <>
      <button type="button" onClick={() => setVisible(true)} style={TRIGGER}>
        {selectedSkill ? (
          <SkillCatalogIcon skill={selectedSkill} size={28} />
        ) : null}
        <span style={{ flex: 1, minWidth: 0, textAlign: "start" }}>
          <span style={TRIGGER_LABEL}>{selectedLabel}</span>
          {selectedSkill ? (
            <span style={TRIGGER_HINT}>
              {t("cv.create.tailor.skillPickerChange")}
            </span>
          ) : null}
        </span>
        <MaterialSymbol
          name="chevron_right"
          size={20}
          style={{ color: "var(--text-tertiary)", flexShrink: 0 }}
        />
      </button>

      <WebDialog
        visible={visible}
        title={t("cv.create.tailor.skillPickerTitle")}
        subtitle={t("cv.create.tailor.skillPickerSubtitle")}
        onClose={() => setVisible(false)}
        maxWidth={680}
        footer={
          <div style={FOOTER}>
            {activeFilterCount > 0 ? (
              <button
                type="button"
                onClick={() => setFilters(DEFAULT_CV_SKILL_PICKER_FILTERS)}
                style={SECONDARY_BUTTON}
              >
                {t("common:skills.filters.clear")}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setVisible(false)}
              style={PRIMARY_BUTTON}
            >
              {t("common:skills.runDialog.doneButton")}
            </button>
          </div>
        }
      >
        <div style={DIALOG_CONTENT}>
          <label style={SEARCH_BAR}>
            <MaterialSymbol
              name="search"
              size={20}
              style={{ color: "var(--text-secondary)", flexShrink: 0 }}
            />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("cv.create.tailor.skillSearchPlaceholder")}
              aria-label={t("cv.create.tailor.skillSearchPlaceholder")}
              style={SEARCH_INPUT}
            />
            {query ? (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label={t("common:close")}
                style={ICON_BUTTON}
              >
                <MaterialSymbol name="close" size={17} />
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setFiltersOpen((current) => !current)}
              aria-label={t("cv.create.tailor.skillFilters")}
              style={FILTER_ICON_BUTTON}
            >
              <MaterialSymbol name="tune" size={20} />
              {activeFilterCount > 0 ? <span style={FILTER_BADGE}>{activeFilterCount}</span> : null}
            </button>
          </label>

          {filtersOpen ? <div style={FILTER_BAR}>
            <FilterDropdown<CvSkillCountryFilter>
              label={t("common:skills.detail.country")}
              value={filters.country}
              defaultValue="all"
              onChange={(value) => updateFilter("country", value)}
              options={[
                {
                  value: "all",
                  label: t("common:skills.filterBar.allCountries"),
                },
                ...CV_SKILL_COUNTRY_FILTERS.map((value) => ({
                  value,
                  label: t(`common:skills.countries.${value}`),
                })),
              ]}
            />

            <FilterDropdown<SkillLevel>
              label={t("common:skills.detail.level")}
              value={filters.level}
              defaultValue="all"
              onChange={(value) => updateFilter("level", value)}
              options={[
                {
                  value: "all",
                  label: t("common:skills.filterBar.allLevels"),
                },
                ...CV_SKILL_LEVEL_FILTERS.map((value) => ({
                  value,
                  label: t(`common:skills.levels.${value}`),
                })),
              ]}
            />

            <FilterDropdown<CvSkillLanguageFilter>
              label={t("common:skills.detail.languages")}
              value={filters.language}
              defaultValue="any"
              align="end"
              onChange={(value) => updateFilter("language", value)}
              options={[
                {
                  value: "any",
                  label: t("common:skills.filterBar.anyLanguage"),
                },
                ...CV_SKILL_LANGUAGE_FILTERS.map((value) => ({
                  value,
                  label: t(
                    `common:skills.filterBar.${
                      value === "en"
                        ? "langEn"
                        : value === "ar"
                          ? "langAr"
                          : "langBi"
                    }`,
                  ),
                })),
              ]}
            />
          </div> : null}

          <div style={RESULTS}>
            {skillsQuery.isLoading ? (
              <p style={STATUS}>{t("common:loading")}</p>
            ) : fitting.length === 0 ? (
              <p style={STATUS}>
                {skillsQuery.isError
                  ? t("cv.create.tailor.skillsError")
                  : t("cv.create.tailor.skillsEmpty")}
              </p>
            ) : shown.length === 0 ? (
              <p style={STATUS}>{t("cv.create.tailor.skillSearchEmpty")}</p>
            ) : (
              shown.map((skill) => {
                const selected = skill.skillId === selectedSkillId;
                const copy = localizedCvSkillCopy(skill, i18n.language);
                return (
                  <div
                    key={skill.id}
                    role="radio"
                    aria-checked={selected}
                    onClick={() => {
                      onSelect(skill);
                      setVisible(false);
                    }}
                    style={{
                      ...SKILL_ROW,
                      borderColor: selected
                        ? "var(--text-secondary)"
                        : "var(--input-border)",
                      background: selected ? "var(--hover)" : "var(--bg)",
                    }}
                  >
                    <SkillCatalogIcon skill={skill} size={40} />
                    <span style={SKILL_COPY}>
                      <span style={SKILL_NAME_ROW}>
                        <span style={SKILL_NAME}>
                          {prettySkillName(copy.name)}
                        </span>
                        {skill.verified ? <TabbioVerifiedBadge compact /> : null}
                      </span>
                      {copy.description ? (
                        <span style={SKILL_DESCRIPTION}>
                          {copy.description}
                        </span>
                      ) : null}
                      <span style={SKILL_INSTALLS}>
                        {skill.authorName || t("common:skills.byTabbioName")} ·
                        <Download size={13} />
                        {t("common:skills.usageCount", {
                          value: formatSkillCount(skill.usageCount, true),
                        })}
                      </span>
                    </span>
                    {selected ? (
                      <MaterialSymbol
                        name="check_circle"
                        size={19}
                        style={{ color: "var(--text-primary)", flexShrink: 0 }}
                      />
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </WebDialog>

    </>
  );
}

const TRIGGER: CSSProperties = {
  width: "100%",
  minHeight: 48,
  display: "flex",
  alignItems: "center",
  gap: 10,
  border: "1px solid var(--input-border)",
  borderRadius: 16,
  padding: "10px 14px",
  background: "var(--bg)",
  color: "var(--text-primary)",
  cursor: "pointer",
  fontFamily: "inherit",
};
const TRIGGER_LABEL: CSSProperties = {
  display: "block",
  overflow: "hidden",
  color: "var(--text-primary)",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "19px",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const TRIGGER_HINT: CSSProperties = {
  display: "block",
  marginTop: 2,
  color: "var(--text-tertiary)",
  fontSize: 12,
  lineHeight: "16px",
};
const DIALOG_CONTENT: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
};
const SEARCH_BAR: CSSProperties = {
  minHeight: 48,
  display: "flex",
  alignItems: "center",
  gap: 10,
  border: "1px solid var(--input-border)",
  borderRadius: 999,
  paddingInline: 16,
  background: "var(--bg)",
  boxShadow: "var(--gem-input-shadow)",
};
const SEARCH_INPUT: CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: 0,
  outline: 0,
  background: "transparent",
  color: "var(--text-primary)",
  font: "inherit",
  fontSize: 14,
};
const ICON_BUTTON: CSSProperties = {
  width: 30,
  height: 30,
  border: 0,
  borderRadius: 999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  color: "var(--text-tertiary)",
  cursor: "pointer",
};
const FILTER_ICON_BUTTON: CSSProperties = { ...ICON_BUTTON, position: "relative", color: "var(--text-secondary)", cursor: "pointer" };
const FILTER_BADGE: CSSProperties = { position: "absolute", top: -3, insetInlineEnd: -3, minWidth: 16, height: 16, paddingInline: 3, borderRadius: 8, display: "grid", placeItems: "center", background: "#5e29ff", color: "#fff", fontSize: 10, fontWeight: 700 };
const FILTER_BAR: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: 8,
};
const FILTER_DROPDOWN_ROOT: CSSProperties = {
  position: "relative",
  minWidth: 0,
};
const FILTER_CHIP: CSSProperties = {
  width: "100%",
  height: 40,
  border: "1px solid var(--input-border)",
  borderRadius: 999,
  paddingInline: 13,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 6,
  background: "var(--bg)",
  color: "var(--text-primary)",
  cursor: "pointer",
  font: "inherit",
  fontSize: 13,
  transition: "background 150ms, border-color 150ms, transform 150ms",
};
const FILTER_CHIP_TEXT: CSSProperties = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const FILTER_MENU: CSSProperties = {
  position: "fixed",
  // Above the WebDialog overlay (zIndex 1000); top/inline edge/width/maxHeight
  // are measured from the chip when the menu opens.
  zIndex: 1100,
  overflowY: "auto",
  border: "1px solid var(--input-border)",
  borderRadius: 14,
  padding: 5,
  background: "var(--menu-bg)",
  boxShadow: "var(--gem-menu-shadow)",
};
const FILTER_MENU_OPTION: CSSProperties = {
  width: "100%",
  minHeight: 38,
  border: 0,
  borderRadius: 9,
  padding: "8px 10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  background: "transparent",
  color: "var(--text-primary)",
  cursor: "pointer",
  font: "inherit",
  fontSize: 13,
  textAlign: "start",
};
const RESULTS: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
  marginTop: 4,
};
const SKILL_ROW: CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  border: "1px solid var(--input-border)",
  borderRadius: 16,
  padding: "12px 14px",
  color: "var(--text-primary)",
  cursor: "pointer",
  fontFamily: "inherit",
};
const SKILL_COPY: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 2,
  textAlign: "start",
};
const SKILL_NAME: CSSProperties = {
  color: "var(--text-primary)",
  fontSize: 14,
  fontWeight: 500,
  lineHeight: "19px",
};
const SKILL_DESCRIPTION: CSSProperties = {
  display: "-webkit-box",
  overflow: "hidden",
  color: "var(--text-secondary)",
  fontSize: 12.5,
  lineHeight: "17px",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 2,
};
const SKILL_NAME_ROW: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  minWidth: 0,
};
const SKILL_INSTALLS: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  marginTop: 6,
  color: "var(--text-tertiary)",
  fontSize: 12,
  lineHeight: "16px",
};
const STATUS: CSSProperties = {
  margin: 0,
  padding: "20px 8px",
  color: "var(--text-tertiary)",
  fontSize: 13,
  textAlign: "center",
};
// Flex, not a fixed two-column grid: when there are no active filters the
// "Clear filters" button is absent, and the grid left Done stranded at half
// width in column one.
const FOOTER: CSSProperties = {
  display: "flex",
  gap: 10,
};
const SECONDARY_BUTTON: CSSProperties = {
  flex: 1,
  minHeight: 44,
  border: "1px solid var(--input-border)",
  borderRadius: 999,
  background: "var(--bg)",
  color: "var(--text-primary)",
  cursor: "pointer",
  font: "inherit",
  fontWeight: 500,
};
const PRIMARY_BUTTON: CSSProperties = {
  ...SECONDARY_BUTTON,
  borderColor: "var(--text-primary)",
  background: "var(--text-primary)",
  color: "var(--bg)",
};
